import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const COST_PER_SECOND = 0.05 * 1_000_000 * (1 + BRAND_MARKUP);

const ASPECT_RATIO_OPTIONS = [
  { label: "9:16", value: "9:16" },
  { label: "16:9", value: "16:9" },
  { label: "1:1", value: "1:1" },
  { label: "4:3", value: "4:3" },
  { label: "3:4", value: "3:4" },
  { label: "3:2", value: "3:2" },
  { label: "2:3", value: "2:3" },
] as const;

const DURATION_OPTIONS = [
  { label: "5s", value: "5" },
  { label: "8s", value: "8" },
  { label: "10s", value: "10" },
  { label: "15s", value: "15" },
] as const;

const RESOLUTION_OPTIONS = [
  { label: "480p", value: "480p" },
  { label: "720p", value: "720p" },
] as const;

const grokImagineVideoParams = z
  .object({
    prompt: z.string().default(""),
    aspectRatio: z
      .enum(["9:16", "16:9", "1:1", "4:3", "3:4", "3:2", "2:3"])
      .default("9:16"),
    duration: z.enum(["5", "8", "10", "15"]).default("8"),
    resolution: z.enum(["480p", "720p"]).default("720p"),
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const grokImagineVideo = defineModel({
  key: "grok-imagine-video",
  stepType: "video",
  spec: {
    label: "Grok Imagine Video",
    description:
      "Video generation by xAI with optional image input for image-to-video",
    icon: "xai-icon.svg",
    tags: ["video-gen", "image-to-video", "text-to-video"],
    featured: true,
    info: "Generation takes 1-5 minutes. Optionally connect a Start Image for image-to-video.",
    features: {
      aspectRatio: true,
      duration: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      aspectRatio: { items: ASPECT_RATIO_OPTIONS },
      duration: { items: DURATION_OPTIONS },
      resolution: { items: RESOLUTION_OPTIONS },
      attachments: [
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Start Image",
          slot: "input_image",
          required: false,
        },
      ],
    },
    params: grokImagineVideoParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/xai/grok-imagine-video/text-to-video",
    entries: [
      { unit: "per_second", unitPriceMicrodollars: 50_000, variant: "480p" },
      { unit: "per_second", unitPriceMicrodollars: 70_000, variant: "720p" },
    ],
  },
  providers: [
    {
      provider: "xai",
      modelId: "grok-imagine-video",
      estimateCost(params) {
        const duration = Number(params.duration) || 8;
        return Math.ceil(COST_PER_SECOND * duration);
      },
      costPerSecond: COST_PER_SECOND,
      toPayload(params) {
        const imageUrl = params.attachments?.find(
          (att) => att.slot === "input_image",
        )?.data;

        return {
          model: "grok-imagine-video" as const,
          prompt: params.prompt,
          duration: Number(params.duration) || 8,
          aspect_ratio: params.aspectRatio,
          resolution: params.resolution,
          ...(imageUrl ? { image: { url: imageUrl } } : {}),
        };
      },
    },
  ],
});
